import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/Dmservices.css";

import dm1 from "../assets/icon1.png";
import dm2 from "../assets/icon2.png";
import dm3 from "../assets/icon3.png";
import dm4 from "../assets/icon4.png";
import dm5 from "../assets/icon5.png";
import dm6 from "../assets/icon6.png";

const services = [
  {
    icon: dm1,
    title: "Search Engine Optimization (SEO)",
    desc: "Improve your website's visibility on Google with comprehensive SEO services including keyword research, on-page optimization, technical SEO, and high-quality link building to generate sustainable organic traffic.",
    tags: [
      "Technical SEO",
      "On-Page SEO",
      "Keyword Research",
      "Link Building",
      "Organic Traffic",
    ],
  },
  {
    icon: dm2,
    title: "Pay-Per-Click Advertising (PPC)",
    desc: "Generate instant leads with professionally managed Google Ads and paid advertising campaigns. We optimize every campaign to maximize conversions while minimizing advertising costs.",
    tags: [
      "Google Ads",
      "PPC Campaigns",
      "Lead Generation",
      "Conversion Optimization",
      "Paid Advertising",
    ],
  },
  {
    icon: dm3,
    title: "Social Media Marketing",
    desc: "Build brand awareness and engage your audience through strategic social media marketing on Facebook, Instagram, LinkedIn, and other leading platforms.",
    tags: [
      "Social Media Strategy",
      "Facebook Marketing",
      "Instagram Marketing",
      "Brand Awareness",
      "Audience Engagement",
    ],
  },
  {
    icon: dm4,
    title: "Google Business Profile Optimization",
    desc: "Increase local visibility with Google Business Profile optimization. We improve your business listing, local search rankings, customer engagement, and map visibility to attract nearby customers.",
    tags: [
      "Local SEO",
      "Google Business Profile",
      "Maps Ranking",
      "Customer Reviews",
      "Business Visibility",
    ],
  },
  {
    icon: dm5,
    title: "Email Marketing",
    desc: "Strengthen customer relationships through personalized email campaigns that nurture leads, improve customer retention, and drive repeat sales with targeted communication.",
    tags: [
      "Email Campaigns",
      "Newsletter Marketing",
      "Lead Nurturing",
      "Customer Retention",
      "Marketing Automation",
    ],
  },
  {
    icon: dm6,
    title: "Content Marketing",
    desc: "Attract, educate, and convert customers with high-quality content including blogs, landing pages, website copy, and marketing content designed to improve SEO and build brand authority.",
    tags: [
      "Content Strategy",
      "Blog Writing",
      "SEO Content",
      "Brand Authority",
      "Website Content",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function ServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = services.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="dm-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="dm-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`dm-services-eyebrow ${
          sectionVisible ? "dm-reveal" : ""
        }`}
      >
        What We Offer
      </span>

      <h2
        className={`dm-services-heading ${
          sectionVisible ? "dm-reveal" : ""
        }`}
        ref={headingRef}
      >
        Why Your Business Needs Professional Digital Marketing Services
      </h2>

      <p
        className={`dm-services-subtext ${
          sectionVisible ? "dm-reveal" : ""
        }`}
        ref={subtextRef}
      >
        Our comprehensive digital marketing solutions help businesses
        strengthen their online presence, attract targeted customers,
        improve search engine rankings, and increase conversions through
        proven marketing strategies.
      </p>

      <div className="dm-carousel">
        <button
          type="button"
          className="dm-carousel-arrow dm-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="dm-carousel-track">
          {services.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`dm-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="dm-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="dm-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="dm-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="dm-carousel-arrow dm-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="dm-carousel-dots">
        {services.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`dm-dot ${index === activeIndex ? "is-active" : ""}`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default ServicesSection;